import {
    TDDFilterExpression,
    TDDFilterExpressionDto,
    IDDFilterOperandObject,
    TTransformExpressionArrayToFilterExpressionDtoFunction,
} from '../model/types';

/**
 * Преобразует массивное представление выражения фильтра обратно в DTO объект
 * 
 * @description Выполняет обратное преобразование по отношению к transformFilterExpressionDtoToExpressionArray.
 * Рекурсивно обходит массивное представление фильтра, восстанавливая иерархическую структуру DTO.
 * В процессе преобразования восстанавливает или присваивает порядковые номера (ordinal) элементам,
 * если они отсутствуют, используя их позицию в массиве.
 * 
 * @param expression - Массивное представление выражения фильтра
 * @returns DTO объект фильтра с восстановленной иерархической структурой,
 *          содержащий поля идентификатора, правила фильтрации и вложенные группы
 * 
 * @example
 * // Пример входных данных:
 * const expression = [
 *   { id: 'group1', ordinal: 1, enabled: true, connectBy: 'AND', uid: 'uuid-1234' },
 *   { field: 'name', operator: 'eq', value: 'test', ordinal: 1, uid: 'uuid-5678' },
 *   [
 *     { id: 'group2', ordinal: 2, enabled: true, connectBy: 'OR', uid: 'uuid-9012' },
 *     { field: 'age', operator: 'gt', value: 18, ordinal: 1, uid: 'uuid-3456' }
 *   ]
 * ];
 * 
 * // Результат:
 * // {
 * //   id: 'group1',
 * //   ordinal: 1,
 * //   enabled: true,
 * //   connectBy: 'AND',
 * //   rules: [{ field: 'name', operator: 'eq', value: 'test', ordinal: 1 }],
 * //   groups: [{
 * //     id: 'group2',
 * //     ordinal: 2,
 * //     enabled: true,
 * //     connectBy: 'OR',
 * //     rules: [{ field: 'age', operator: 'gt', value: 18, ordinal: 1 }],
 * //     groups: []
 * //   }]
 * // }
 */
export const transformExpressionArrayToFilterExpressionDtoFunction: TTransformExpressionArrayToFilterExpressionDtoFunction =
    (expression: TDDFilterExpression) => {
        const expressionWithOrdinals = expression.map((exp, index) => {
            if (!Array.isArray(exp)) {
                return {...exp, ordinal: exp?.ordinal ?? index};
            }

            const [identifier, ...operands] = exp;
            const identifierWithOrdinal = {...identifier, ordinal: identifier?.ordinal ?? index};
            return [identifierWithOrdinal, ...operands];
        }) as TDDFilterExpression;

        const [identifier, ...operands] = expressionWithOrdinals;

        const expressionArrays = operands.filter((oper) => Array.isArray(oper)) as TDDFilterExpression[];
        const rules = operands.filter((oper) => !Array.isArray(oper)) as IDDFilterOperandObject[];

        const groups: TDDFilterExpressionDto[] = [];

        (expressionArrays ?? []).forEach((expArray) => {
            groups.push(transformExpressionArrayToFilterExpressionDtoFunction(expArray));
        });

        return {...identifier, groups, rules};
    };
